<template>
  <IndexPanel
    v-if="listItems.length > 0"
    :filterByOptions="filterByOptions"
    :initialData="listItems"
    :modelValue="searchResults"
    operationType="equipments"
  />
  <OperationList
    v-slot="slotProps"
    :listItems="searchResults"
    operationType="equipments"
  >
    <OperationList
      v-if="slotProps && slotProps.item.listItem.mines"
      v-slot="slotProps2"
      innerGroup
      :listItems="slotProps.item.listItem.mines"
      operationType="equipments"
    >
      <OperationItem
        v-for="item in slotProps2.item.listItem.equipments"
        :id="item.id"
        :key="item.id"
        barChart
        endpointIdentifier="equipment"
        :itemRoutes="{
          view: { name: 'equipment', params: { equipmentId: item.id } },
          edit: { name: 'equipmentConfigurationEquipment', params: { organisationId: props.organisationId, equipmentId: item.id } },
        }"
        :organisationId="organisationId"
        @reload="reload"
      />
    </OperationList>
  </OperationList>
</template>

<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import useAjax from '@/functions/use_ajax';
import I18n from 'i18n-js';

// Components
import OperationList from '@/components/operations/operations_list.vue';
import OperationItem from '@/components/operations/operation_item.vue';
import IndexPanel from '@/components/form/index_panel.vue';

const emit = defineEmits(['update']);

const props = defineProps({
  organisationId: { type: String, default: null },
});

const ajax = useAjax();
const listItems = ref([]);
const loading = ref(true);
const searchResults = ref([]);
const types = ref([]);
const mines = ref([]);
const processes = ref([]);
const filterByOptions = ref([]);

const itemCount = computed(() => {
  let mineEquipmentLength = 0;
  return searchResults.value?.reduce(function (_, obj) {
    mineEquipmentLength += obj.mines?.reduce(function (acc, obj2) {
      return acc + (obj2.equipments?.length || 0);
    }, 0);
    return mineEquipmentLength;
  }, 0);
});

// All endpoints that this component uses
const ENDPOINTS = {
  equipment: `/organisations/${props.organisationId}/equipment`,
  types: `/organisations/${props.organisationId}/equipment_types`,
  processes: `/organisations/${props.organisationId}/equipment_processes`,
  mines: `/organisations/${props.organisationId}/mines`,
};

onMounted(load);
watch(props, load);
watch(itemCount, emitCount);

function emitCount() {
  emit('update', {
    count: itemCount.value || 0,
    label: 'equipment',
    loading: loading.value,
  });
}

async function load() {
  loadEquipment(await loadData(ENDPOINTS.equipment));
  loading.value = false;
  emitCount();
}

async function setUniqueFilterValues(list, id, validate = true) {
  if (!id && validate) return;
  const exists = list.filter(item => item.value === id).length > 0;
  !exists && list.push({ value: id, label: null });
}

async function setUniqueFilterNames(list, url) {
  list.forEach(async (item, index) => {
    if (!item.value) return (list[index].label = I18n.t('nav.none')); // Add Unclassified mine
    const data = await loadData(`${url}/${item.value}`);
    list[index].label = data?.name;
  });
}

async function setFilterOptions(list, key) {
  list.length > 0 && (filterByOptions.value[key] = list);
}

async function loadEquipment(data) {
  // Rename the 'equipments' operation name to 'HME' for filtering purposes
  const foundEquipment = data.divisions.find(item => item.name === 'equipments');
  foundEquipment && (foundEquipment.name = 'HME');

  listItems.value = JSON.parse(JSON.stringify(data.divisions));
  searchResults.value = data.divisions;

  prepareFilterOptions(data.divisions);
}

function prepareFilterOptions(divisions) {
  divisions.forEach(division => {
    division.mines.forEach(mine => {
      mine.equipments.forEach(equipment => {
        !equipment.mine_id && setUniqueFilterValues(mines.value, null, false); // Add Unclassified mine
        setUniqueFilterValues(mines.value, equipment.mine_id);
        setUniqueFilterValues(processes.value, equipment.equipment_process_id);
        setUniqueFilterValues(types.value, equipment.equipment_type_id);
      });
    });
  });

  setUniqueFilterNames(mines.value, ENDPOINTS.mines);
  setUniqueFilterNames(processes.value, ENDPOINTS.processes);
  setUniqueFilterNames(types.value, ENDPOINTS.types);

  // Don't set mine list if the only option is 'Unclassified mine'
  const isOnlyUnspecifiedMine = mines.value?.length === 1 && !mines.value[0].value;
  !isOnlyUnspecifiedMine && setFilterOptions(mines.value, 'mine');
  setFilterOptions(processes.value, 'process');
  setFilterOptions(types.value, 'type');
}

async function loadData(url) {
  let { ok, data } = await ajax(url);

  if (ok) {
    return data;
  }
}

function reload(data) {
  if (data?.type === 'delete') {
    return listItems.value.forEach((division, divisionIndex) => {
      division.mines.forEach((mine, mineIndex) => {
        mine.equipments = mine?.equipments.filter(operation => operation.id !== data?.id);
        searchResults.value[divisionIndex].mines[mineIndex].equipments = searchResults.value[divisionIndex].mines[mineIndex]?.equipments.filter(operation => operation.id !== data?.id);
      });
    });
  }
  /**
   * TODO: remove when we find a better way to know
   * when the clone process has added an item to the database
   */
  setTimeout(() => {
    load();
  }, 3000);
}
</script>
